import { useReducer } from 'react';
import moment from 'moment';
import { InAppSaleType, OrderStatus, ReservationType } from 'lib/models/order';

export type SoldProductsFiltersState = {
	from: moment.Moment | null;
	to: moment.Moment | null;
	confirmationFrom: moment.Moment | null;
	confirmationTo: moment.Moment | null;
	cancellationFrom: moment.Moment | null;
	cancellationTo: moment.Moment | null;
	reservationFrom: moment.Moment | null;
	reservationTo: moment.Moment | null;
	selectedStatuses: OrderStatus[];
	selectedVendors: string[];
	selectedSections: string[];
	isInAppSale: boolean | null;
	inAppSaleType: InAppSaleType | '';
	reservationType: ReservationType | '';
};

export type FilterAction =
	| { type: 'SET_FROM'; payload: moment.Moment | null }
	| { type: 'SET_TO'; payload: moment.Moment | null }
	| { type: 'SET_CONFIRMATION_FROM'; payload: moment.Moment | null }
	| { type: 'SET_CONFIRMATION_TO'; payload: moment.Moment | null }
	| { type: 'SET_CANCELLATION_FROM'; payload: moment.Moment | null }
	| { type: 'SET_CANCELLATION_TO'; payload: moment.Moment | null }
	| { type: 'SET_RESERVATION_FROM'; payload: moment.Moment | null }
	| { type: 'SET_RESERVATION_TO'; payload: moment.Moment | null }
	| { type: 'SET_SELECTED_STATUSES'; payload: OrderStatus[] }
	| { type: 'SET_SELECTED_VENDORS'; payload: string[] }
	| { type: 'SET_SELECTED_SECTIONS'; payload: string[] }
	| { type: 'SET_IS_IN_APP_SALE'; payload: boolean | null }
	| { type: 'SET_INAPP_SALE_TYPE'; payload: InAppSaleType | '' }
	| { type: 'SET_RESERVATION_TYPE'; payload: ReservationType | '' }
	| { type: 'CLEAR_DATE_FILTERS' }
	| { type: 'CLEAR_OTHER_FILTERS' };

const initialState: SoldProductsFiltersState = {
	from: null,
	to: null,
	confirmationFrom: null,
	confirmationTo: null,
	cancellationFrom: null,
	cancellationTo: null,
	reservationFrom: null,
	reservationTo: null,
	selectedStatuses: [OrderStatus.closed],
	selectedVendors: [],
	selectedSections: [],
	isInAppSale: null,
	inAppSaleType: '',
	reservationType: '',
};

function filtersReducer(state: SoldProductsFiltersState, action: FilterAction): SoldProductsFiltersState {
	switch (action.type) {
		case 'SET_FROM':
			return { ...state, from: action.payload };
		case 'SET_TO':
			return { ...state, to: action.payload };
		case 'SET_CONFIRMATION_FROM':
			return { ...state, confirmationFrom: action.payload };
		case 'SET_CONFIRMATION_TO':
			return { ...state, confirmationTo: action.payload };
		case 'SET_CANCELLATION_FROM':
			return { ...state, cancellationFrom: action.payload };
		case 'SET_CANCELLATION_TO':
			return { ...state, cancellationTo: action.payload };
		case 'SET_RESERVATION_FROM':
			return { ...state, reservationFrom: action.payload };
		case 'SET_RESERVATION_TO':
			return { ...state, reservationTo: action.payload };
		case 'SET_SELECTED_STATUSES':
			return { ...state, selectedStatuses: action.payload };
		case 'SET_SELECTED_VENDORS':
			return { ...state, selectedVendors: action.payload };
		case 'SET_SELECTED_SECTIONS':
			return { ...state, selectedSections: action.payload };
		case 'SET_IS_IN_APP_SALE':
			return { ...state, isInAppSale: action.payload, inAppSaleType: action.payload !== true ? '' : state.inAppSaleType };
		case 'SET_INAPP_SALE_TYPE':
			return { ...state, inAppSaleType: action.payload };
		case 'SET_RESERVATION_TYPE':
			return { ...state, reservationType: action.payload };
		case 'CLEAR_DATE_FILTERS':
			return {
				...state,
				from: null,
				to: null,
				confirmationFrom: null,
				confirmationTo: null,
				cancellationFrom: null,
				cancellationTo: null,
				reservationFrom: null,
				reservationTo: null,
			};
		case 'CLEAR_OTHER_FILTERS':
			return {
				...state,
				selectedStatuses: [OrderStatus.closed],
				selectedVendors: [],
				selectedSections: [],
				isInAppSale: null,
				inAppSaleType: '',
				reservationType: '',
			};
		default:
			return state;
	}
}

export function useSoldProductsFilters(initialOverrides?: Partial<SoldProductsFiltersState>) {
	const [filters, dispatch] = useReducer(filtersReducer, {
		...initialState,
		...initialOverrides,
	});

	return { filters, dispatch };
}
